import React, { Suspense, Fragment, useEffect } from "react";
import { Segment, Header, Grid, Icon, Divider, Responsive, Button, Sidebar, Menu } from "semantic-ui-react";
import { useTranslation } from "react-i18next";

import { PageTemplate } from "../components/PageTemplate/PageTemplate";
import { AuditsList } from "../components/audits/AuditsList/index";
import { TFunction } from "i18next";
import { AuditsTable } from "../components/audits/AuditsTable";
import { AuditFiltersForm } from "../components/audits/filters/AudFiltersForm";
import { IAuditListFilters } from "../interfaces/IAudit";
import { IPagination } from "../interfaces/common/IPagination";
import { RenderAuditPageItems } from "../components/audits/RenderAuditPageItems";
import { RenderAuditPage } from "../components/audits/RenderAuditPage";
import { useBool } from "../../sg-hooks/useBool";
import TablePagination from "../components/audits/AuditsTable/TablePagination";
import { useAuditListFiltersContext } from "../context/AuditFilterContext";

const getTextProps = (t: TFunction) => ({
    safetyAudits: t("Turvallisuusauditoinnit"),
    audits: t("Auditoinnit"),
    filterAudits: t("Suodata"),
    close: t("Sulje"),
    noResults: t("Ei tuloksia")
});

interface AuditListPageContentProps extends ReturnType<typeof getTextProps> {
    filters: IAuditListFilters;
    onFiltersChanged: (newFilters: IAuditListFilters) => void;

    pagination: IPagination;
    onPaginationChanged: (pagination: IPagination) => void;
};

//
//  Desktop
//

const AuditListPageDesktopContent: React.FC<AuditListPageContentProps> = ({
    pagination,
    onPaginationChanged,

    filters,
    onFiltersChanged,
    ...texts
}) => {
    return (
        <Fragment>
            <Divider hidden />
            <Grid columns={1}>
                <Grid.Column floated="left">
                    <Header size="large"> {texts.safetyAudits} </Header>

                    <Suspense fallback={<Fragment />}>
                        <AuditFiltersForm
                            formData={filters}
                            onChange={onFiltersChanged}
                        />
                    </Suspense>
                </Grid.Column>
            </Grid>

            <Suspense fallback={<Segment basic loading placeholder />}>
                <Segment basic>
                    <RenderAuditPageItems
                        filters={filters}
                        initialPagination={pagination}
                        onPaginationChanged={onPaginationChanged}
                        render={({ items, pagination, goToPage }) => {
                            if (items.length < 1) {
                                return <Segment basic children={texts.noResults} />;
                            };
                            return (
                                <AuditsTable
                                    items={items}
                                    pagination={pagination}
                                    goToPage={goToPage}
                                />
                            )
                        }}
                    />
                </Segment>
            </Suspense>
            <Divider hidden />
        </Fragment>
    );
};

//
//  Mobile
//

const AuditListPageMobileContent: React.FC<AuditListPageContentProps> = ({

    filters,

    pagination,

    onFiltersChanged,
    onPaginationChanged,
    ...texts
}) => {

    const [filtersVisible, openFilters, closeFilters] = useBool(false);

    return (
        <Sidebar.Pushable >
            <Sidebar
                className="AuditListPage-sidebar"
                animation="overlay"
                direction="right"
                icon="labeled"
                inverted
                onHide={closeFilters}
                vertical
                visible={filtersVisible}
                width="thin"
            >

                <Suspense fallback={<Fragment />}>
                    <Segment basic>
                        <Header >  {texts.filterAudits} </Header>
                        <Button className="AuditListPage-sidebar-close" size="small" basic icon="times" onClick={closeFilters} />
                        <Divider />
                        <AuditFiltersForm
                            formData={filters}
                            onChange={onFiltersChanged}
                            inline={false}
                        />

                        <Divider hidden />

                        <Button content={texts.close} onClick={closeFilters} fluid />
                    </Segment>
                </Suspense>
            </Sidebar>

            <Sidebar.Pusher style={{ minHeight: "100vh" }}>

                <Grid columns={1} padded>


                    <Grid.Column style={{ paddingLeft: 0, paddingRight: 0, paddingBottom: 0, paddingTop: 0, marginTop: "15px" }}>
                        <Menu secondary >
                            <Menu.Item>
                                <Header> {texts.audits} </Header>
                            </Menu.Item>

                            <Menu floated="right" secondary>
                                <Menu.Item
                                    style={{ textTransform: "uppercase" }}
                                    //  content={texts.filterAudits}
                                    // icon={<Icon name="filter" />}
                                    onClick={openFilters}
                                >
                                    {texts.filterAudits}{" "}
                                    <Icon
                                        //   name="filter"
                                        name="ellipsis vertical"
                                    />
                                </Menu.Item>
                            </Menu>
                        </Menu>
                    </Grid.Column>



                </Grid>


                <Divider style={{ marginBottom: 0 }} />

                {/*
                <SelectedFilters
                    filters={filters}
                    onChange={updateFilters}
                />
                 */}

                <Segment basic style={{ padding: 0 }}>
                    <Suspense fallback={<Segment loading basic style={{ minHeight: "400px" }} />}>
                        <RenderAuditPage
                            filters={filters}
                            initialPagination={pagination}
                            onPaginationChanged={onPaginationChanged}
                            render={({ page, pagination: pg, goToPage }) => {
                                const totalPages = Math.ceil((pg.totalItems || 0) / pg.pageSize);
                                if (page.length < 1) {
                                    return (
                                        <Segment basic textAlign="center" children={texts.noResults} />
                                    );
                                };

                                //  const changePage: PaginationProps["onPageChange"] = (_1, { activePage }) => goToPage(Number(activePage));

                                return (
                                    <Suspense fallback={<Segment loading basic />}>
                                        <AuditsList audits={page} />

                                        <Divider hidden />
                                        {totalPages > 1 ? (
                                            <TablePagination
                                                activePage={pg.page}
                                                onPageChange={goToPage}
                                                totalPages={totalPages}
                                                ellipsisItem={null}
                                                firstItem={null}
                                                lastItem={null}
                                                siblingRange={1}
                                                size="small"
                                                basic="very"
                                            />
                                        ) : null}
                                        <Divider hidden />
                                        <Divider hidden />
                                    </Suspense>
                                )
                            }}
                        />
                    </Suspense>
                </Segment>

            </Sidebar.Pusher>
        </Sidebar.Pushable>
    );
};

//
//  Page
//

const AuditListPage: React.FC = () => {
    const textProps = getTextProps(useTranslation().t);

    const {
        filters,
        setFilters,
        pagination,
        setPagination
    } = useAuditListFiltersContext();

    useEffect(() => { window.scrollTo(0, 0); }, [pagination.page])

    const contentProps: AuditListPageContentProps = {
        filters,
        onFiltersChanged: setFilters,

        pagination,
        onPaginationChanged: setPagination,
        ...textProps
    };

    return (
        <Suspense fallback={<Segment loading placeholder basic />}>
            <PageTemplate id="audit-list-page">
                <Responsive minWidth={1200} >
                    <AuditListPageDesktopContent
                        {...contentProps}
                        pagination={{ ...pagination, pageSize: 10 }}
                    />
                </Responsive>

                <Responsive maxWidth={1200}>
                    <AuditListPageMobileContent
                        {...contentProps}
                        pagination={{ ...pagination, pageSize: 10 }}
                    />
                </Responsive>

            </PageTemplate>
        </Suspense>
    )
};

export default AuditListPage;
